/// <amd-module name="VSS/Features/SecurityUI/Roles/Components/RoleAssignment" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";

import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";

import * as Resources from "VSS/Features/SecurityUI/Roles/Resources";
import { IAddRoleAssignmentDialogProps } from "VSS/Features/SecurityUI/RolesDialog/AddRoleAssignmentDialog.Types";

import { getService } from "../RolesManagementService";
import { IRoleAssignmentProps } from "./RoleAssignment.Props";
import { RoleAssignmentTable } from "./RoleAssignmentTable";
import { RoleAssignmentToolbar } from "./RoleAssignmentToolbar";

export class RoleAssignment extends VssComponent<IRoleAssignmentProps> {
    private showAddUserDialog = new ObservableValue(false);

    public render(): JSX.Element {
        const {
            addUserDialogUserLabel,
            canEdit,
            enableToggleInheritence,
            hideRoleDescriptions,
            manageRolesPermission,
            noPermissionMessage,
            operationScopes = ["ims", "source"],
            resourceId,
            scopeId,
            serviceInstanceId,
            showAvatars = true,
            userId
        } = this.props;

        const identityTypes = this.props.identityTypes ?? ["user", "group"];

        const { pageContext } = this.context;
        const rolesManagementService = getService(pageContext);
        const model = rolesManagementService.getRoleAssignments(
            pageContext,
            scopeId,
            resourceId,
            { canEdit, manageRolesPermission, userId },
            serviceInstanceId
        );

        return (
            <div className={css("security-role-assignment", this.props.className)}>
                <Observer userCanManageRoles={model.userCanManageRoles()}>
                    {(props: { userCanManageRoles: boolean }) => {
                        return !props.userCanManageRoles ? (
                            <MessageCard severity={MessageCardSeverity.Warning}>
                                {noPermissionMessage || Resources.CannotEditRolesMessage}
                            </MessageCard>
                        ) : null;
                    }}
                </Observer>
                <Observer ready={model.isReady()} userCanManageRoles={model.userCanManageRoles()}>
                    {(props: { ready: boolean; userCanManageRoles: boolean }) => {
                        return (
                            <RoleAssignmentToolbar
                                disabled={!props.userCanManageRoles || !props.ready}
                                enableToggleInheritence={enableToggleInheritence}
                                model={model}
                                onAddUserClick={this.onAddUserClick}
                                resourceId={resourceId}
                                scopeId={scopeId}
                                serviceInstanceId={serviceInstanceId}
                            />
                        );
                    }}
                </Observer>
                <Observer error={model.getError()}>
                    {(props: { error: Error | null }) => {
                        return props.error ? (
                            <MessageCard severity={MessageCardSeverity.Error} onDismiss={() => model.setError(null)}>
                                {props.error.message}
                            </MessageCard>
                        ) : null;
                    }}
                </Observer>
                <Observer ready={model.isReady()} userCanManageRoles={model.userCanManageRoles()}>
                    {(props: { ready: boolean; userCanManageRoles: boolean }) => {
                        return props.ready ? (
                            <RoleAssignmentTable model={model} disabled={!props.userCanManageRoles} showAvatars={showAvatars} userId={userId} />
                        ) : null;
                    }}
                </Observer>
                <Observer showAddUserDialog={this.showAddUserDialog}>
                    {(props: { showAddUserDialog: boolean }) => {
                        return props.showAddUserDialog ? (
                            <WrappedComponent
                                dependencies={["ms.vss-features.security-ui-roles-add-dialog"]}
                                wrappedProps={
                                    {
                                        addUserDialogUserLabel,
                                        hideRoleDescriptions,
                                        identityTypes,
                                        model,
                                        onDismiss: this.onAddUserDismiss,
                                        operationScopes
                                    } as IAddRoleAssignmentDialogProps
                                }
                                wrappedType="vss-security-ui.add-role-assignment"
                            />
                        ) : null;
                    }}
                </Observer>
            </div>
        );
    }

    private onAddUserClick = () => {
        this.showAddUserDialog.value = true;
    };

    private onAddUserDismiss = () => {
        this.showAddUserDialog.value = false;
    };
}

VssComponent.register("vss-security-ui.role-assignment", RoleAssignment);
